import React, { useEffect, useRef, useState, Fragment } from "react";
import { getFontAwesomeIcon } from "../../../utils";
import { AnimatePresence, motion } from "framer-motion";
import SingleLineShimmer from "../../layout/Shimmer/SingleLineShimmer";
interface AdvancedInputProps {
	label?: string;
	labelClasses?: string;
	inputClasses?: string;
	onSelect: (user: any) => void;
	fetchMoreData: () => Promise<void>;
	data: any[]; // The list of options to be rendered, passed in as a prop
	isFetchingMore: boolean; // Loading state for the 'load more' functionality
	hasMorePages: boolean; // If there are more pages to fetch
	onClearInput: () => void;
	findSpecificValue: (value: string) => Promise<any>; // Updated type
	defaultValue?: string;
	noOptionsMessage?: string;
	inputValueCharacterCutoff?: number;
	value?: { uuid: string; name: string } | null;
}

interface GenericOption {
	uuid: string;
	name: string;
	[key: string]: any;
}

const UpdatedAdvancedInput: React.FC<AdvancedInputProps> = ({
	label,
	labelClasses = "absolute bottom-11 block text-sm text-text-body-primary mb-1",
	inputClasses = "w-full h-10 pl-2 pr-3 border border-stroke rounded  focus:border-2 focus:border-supply-blue-400 focus:outline-none focus:ring-0",
	onSelect,
	fetchMoreData,
	data,
	isFetchingMore,
	hasMorePages,
	onClearInput,
	findSpecificValue,
	value,
	noOptionsMessage,
	inputValueCharacterCutoff = 38,
}) => {
	const parentRef = useRef<HTMLDivElement>(null);
	const sentinelRef = useRef<HTMLDivElement>(null);
	const [validValueSelected, setValidValueSelected] = useState<string>("");
	const [inputValue, setInputValue] = useState<string>(""); // Ensure inputValue is always a string
	const [displayValue, setDisplayValue] = useState<string>(""); // State to manage the displayed value
	const [filteredOptions, setFilteredOptions] = useState<GenericOption[]>([]); // Typed as GenericOption[]
	const [showDropdown, setShowDropdown] = useState<boolean>(false); // Toggle dropdown visibility
	const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null); // Use ReturnType instead of number
	const abortControllerRef = useRef<AbortController | null>(null); // Ref to store the AbortController
	const isCancelled = useRef(false);
	const [searchLoading, setSearchLoading] = useState<boolean>(false);
	const [dropdownClicked, setDropdownClicked] = useState<boolean>(false);
	const [showButtons, setShowButtons] = useState<boolean>(true);

	// Handle input changes with debounce and API call
	useEffect(() => {
		// Reset the cancellation flag
		if (inputValue.length < 3) {
			setSearchLoading(true);
		}
		isCancelled.current = false;

		// Clear any existing debounce timer
		if (debounceTimerRef.current) {
			clearTimeout(debounceTimerRef.current);
		}
		// Abort any ongoing API call
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}
		// Do not make the API call if input length is less than 3
		if (inputValue.length < 3) {
			setFilteredOptions([]); // Clear the options if input is too short
			return;
		}

		// Create a new AbortController for the new API call
		const abortController = new AbortController();
		abortControllerRef.current = abortController;

		// Start a new debounce timer
		debounceTimerRef.current = setTimeout(() => {
			if (isCancelled.current) {
				return;
			}

			// Call findSpecificValue with the appropriate arguments
			const fetchData = async () => {
				try {
					const result = await findSpecificValue(inputValue);
					// Check if the fetch was aborted
					if (abortControllerRef.current?.signal.aborted) {
						return;
					}
					// Update the filteredOptions state with the fetched data
					const options = result.map((option: any) => ({
						...option,
						uuid: option.uuid,
						name: `${option._name} ${
							option?.nickname ? `"${option.nickname}"` : ""
						} ${option?.email ? `(${option.email})` : ""}`,
					}));

					setFilteredOptions(options);
				} catch (error) {
					if (abortControllerRef.current?.signal.aborted) {
						console.log("Fetch aborted");
					} else {
						console.error("Error fetching data:", error);
					}
				}
			};
			setSearchLoading(false);
			fetchData();
		}, 500); // Wait for 1 seconds

		// Cleanup function
		return () => {
			// Set the cancellation flag
			isCancelled.current = true;

			// Clear the debounce timer when inputValue changes or component unmounts
			if (debounceTimerRef.current) {
				clearTimeout(debounceTimerRef.current);
			}

			// Abort any ongoing API call
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
		};
	}, [inputValue, findSpecificValue]);

	// Update filteredOptions whenever data or inputValue changes
	useEffect(() => {
		if (inputValue.length < 3) {
			// When inputValue is less than 3 characters, use data prop
			setFilteredOptions([]);
		}
	}, [data, inputValue]);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setInputValue(value);
		setDisplayValue(value);
		if (value.length === 0) {
			clearInput();
		}
		setShowDropdown(true); // Show dropdown when typing
	};

	const clearInput = () => {
		setInputValue(""); // Clear input field
		setDisplayValue(""); // Clear display value
		setFilteredOptions([]); // Clear filtered options
		if (onClearInput) {
			onClearInput(); // Notify parent to reset data
		}

		setValidValueSelected(""); // Clear the selected value

		// Abort any ongoing API call when input is cleared
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}
	};

	// Intersection Observer logic for infinite scroll
	useEffect(() => {
		if (showDropdown) {
			const observer = new IntersectionObserver(
				(entries) => {
					const [entry] = entries;
					if (
						entry.isIntersecting &&
						!isFetchingMore &&
						hasMorePages &&
						inputValue.length < 3
					) {
						fetchMoreData();
					}
				},
				{
					root: parentRef.current,
					rootMargin: "0px",
					threshold: 1.0,
				},
			);

			const sentinel = sentinelRef.current;
			if (sentinel) observer.observe(sentinel);

			return () => {
				if (sentinel) observer.unobserve(sentinel);
				observer.disconnect();
			};
		}
	}, [
		showDropdown,
		fetchMoreData,
		isFetchingMore,
		hasMorePages,
		inputValue.length,
	]);

	const handleOptionSelect = (option: GenericOption) => {
		setValidValueSelected(`${option.name}`);
		setInputValue(`${option.name}`); // Use option.value
		setDisplayValue(formatValue(option.name));
		setShowDropdown(false); // Hide the dropdown after selection
		setShowButtons(true);
		if (onSelect) {
			onSelect(option); // Call the callback function with the selected option
		}

		// Abort any ongoing API call when an option is selected
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}
	};

	const handleBlur = () => {
		if (dropdownClicked) {
			setDropdownClicked(false);
			return;
		}

		if (!validValueSelected) {
			clearInput(); // Clear input if no valid option is selected
		} else {
			setDisplayValue(formatValue(inputValue)); // Format value with ellipsis if necessary
		}
		setShowButtons(true);
		setTimeout(() => setShowDropdown(false), 200);
	};

	const formatValue = (value: string) => {
		if (value.length >= inputValueCharacterCutoff) {
			return `${value.substring(0, inputValueCharacterCutoff)}...`;
		} else {
			return value;
		}
	};

	// Prepare options to display
	const options: GenericOption[] =
		inputValue.length >= 3
			? filteredOptions
			: data.map((user: any) => {
					return {
						uuid: user?.uuid,
						name: `${user._name} ${
							user?.nickname ? `"${user.nickname}"` : ""
						} ${user?.email ? `(${user.email})` : ""}`,
					};
				});

	const shimmerCount = 4;

	// Sync with external value (from React Hook Form)
	useEffect(() => {
		const externalValue = value;

		if (externalValue?.uuid && externalValue?.name) {
			setValidValueSelected(externalValue.name);
			setInputValue(externalValue.name);
			setDisplayValue(externalValue.name);
		} else if (!externalValue) {
			// Clear when value is null/undefined
			setValidValueSelected("");
			setInputValue("");
			setDisplayValue("");
		}
	}, [value]); // Re-run when value changes

	const handleOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
		setDisplayValue(inputValue);
		setShowDropdown(true);
		setShowButtons(false);
		setTimeout(() => {
			e.target.select();
		}, 0);
	};

	const handleToggleMenuOnClick = () => {
		setTimeout(() => {
			setDisplayValue(formatValue(inputValue));
			setShowDropdown(!showDropdown);
		}, 100);
	};

	//// CLASSNAMES ////
	const loadingMenuClasses = `relative flex flex-col items-start justify-center text-primary min-h-14 isolate overflow-hidden`;
	const optionsMenuClasses = `${
		searchLoading ? "" : "border-gray-300"
	} absolute w-full max-h-40 overflow-y-auto border shadow-md font-omnes-regular rounded bg-white z-50`;
	const optionItemClasses = `p-2 cursor-pointer border-b border-gray-100 flex justify-between items-center hover:bg-gray-100`;
	const clearInputButtonClasses = `absolute right-[25px] top-[8px] ${
		inputValue
			? "text-neutral-200 cursor-pointer hover:text-neutral-400"
			: " text-supply-blue-400 pointer-events-none"
	}`;
	const openMenuButtonClasses = `text-supply-blue-400 absolute right-[6px] top-[8px]`;
	const selectedOptionCheckClasses = `absolute w-2.5 h-2.5 fill-current text-supply-blue-400 top-[9px] left-[7px]`;

	return (
		<div className="relative">
			{label && <label className={labelClasses}>{label}</label>}
			{options && (
				<>
					<div
						className={`flex items-center relative font-omnes-regular`}
					>
						<input
							type="text"
							value={displayValue}
							onChange={handleInputChange}
							onFocus={(e) => {
								handleOnFocus(e);
							}}
							placeholder="e.g. 'John Doe'"
							autoComplete="off"
							className={inputClasses}
							onBlur={handleBlur}
						/>
						{showButtons && (
							<>
								<button
									type="button"
									className={clearInputButtonClasses}
									onClick={
										inputValue ? clearInput : undefined
									}
								>
									{getFontAwesomeIcon("times", "solid")}
								</button>

								<button
									type="button"
									className={openMenuButtonClasses}
									onClick={() => handleToggleMenuOnClick()}
								>
									{getFontAwesomeIcon("chevronDown", "solid")}
								</button>
							</>
						)}
					</div>
					<AnimatePresence mode="wait">
						{showDropdown && (
							<motion.div
								ref={parentRef}
								className={optionsMenuClasses}
								onMouseDown={() => setDropdownClicked(true)}
							>
								{options.length > 0
									? options.map((option, index) => (
											<div
												key={`${option.uuid}-${option.name}-${index}`}
												onClick={() =>
													handleOptionSelect(option)
												}
												className={optionItemClasses}
											>
												<span>{option.name}</span>
											</div>
										))
									: !isFetchingMore &&
										!searchLoading &&
										!validValueSelected && (
											<div className="p-2 text-gray-500 text-center">
												{noOptionsMessage}
											</div>
										)}
								{validValueSelected &&
									!isFetchingMore &&
									!searchLoading && (
										<div className="p-2 pl-7 text-text-body-primary text-left">
											<div
												className={
													selectedOptionCheckClasses
												}
											>
												{getFontAwesomeIcon(
													"check",
													"solid",
												)}
											</div>
											{validValueSelected}
										</div>
									)}

								{/* Infinite scroll loader */}
								{isFetchingMore && hasMorePages && (
									<motion.div className={loadingMenuClasses}>
										{Array.from({
											length: shimmerCount,
										}).map((_, index) => (
											<Fragment key={index}>
												<SingleLineShimmer
													randomWidth
													minWidth={2}
													maxWidth={4}
													widthFraction={10}
													height="h-4"
													hasBorderBottom
													borderColor="border-gray-200"
													color={"bg-gray-100"}
												/>
											</Fragment>
										))}
									</motion.div>
								)}

								<div
									ref={sentinelRef}
									className={`${searchLoading ? "" : "h-1"}`}
								></div>
							</motion.div>
						)}
					</AnimatePresence>
				</>
			)}
		</div>
	);
};

export default UpdatedAdvancedInput;
